import { useEffect, useState } from 'react';
import Select from '@mui/material/Select';
import { WarningPanel } from '@backstage/core-components';
import {
  useEntityList,
  useEntityTypeFilter,
} from '@backstage/plugin-catalog-react';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useClearEntityFilters } from '@mb.io/core-components';

const IconComponent = (iconProps: any) => <ExpandMoreIcon {...iconProps} />;

const useStyles = makeStyles(theme => ({
  select: {
    width: '100%',
    maxHeight: '50px',
    borderRadius: '2px',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.70)'
        : '1px solid rgba(0, 0, 0, 0.60)',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(0, 0, 0, 0.16)',
    },
    '&.Mui-disabled': {
      borderBottom: 'transparent',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.12)'
          : 'rgba(0, 0, 0, 0.08)',
    },
    '&.Mui-disabled:before': {
      borderRadius: '2px',
    },
  },
  labelDisabled: {
    color:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.70)'
        : 'rgba(0, 0, 0, 0.38)',
  },
}));

export const EntityTypePicker = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { error, availableTypes, selectedTypes, setSelectedTypes } =
    useEntityTypeFilter();
  const { filters } = useEntityList();
  const { clearFilterState, resetFilters } = useClearEntityFilters();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (filters.type?.value?.length === 0) {
      setSelectedTypes([]);
    }
  }, [filters.type, setSelectedTypes]);

  const items = [
    { value: 'all', label: 'All' },
    ...availableTypes.map((type: string) => ({
      value: type,
      label: type,
    })),
  ];

  useEffect(() => {
    if (clearFilterState.type) {
      setSelectedTypes([]);
    }
  }, [clearFilterState, setSelectedTypes]);

  useEffect(() => {
    setIsDisabled(availableTypes.length === 0 || !filters?.kind);
  }, [availableTypes, filters]);

  if (error) {
    return (
      <WarningPanel
        severity="error"
        title="Could not load Filter for Entity Type picker."
      >
        {error?.message}
      </WarningPanel>
    );
  }

  const getValue = () => {
    if (!filters?.kind) {
      return 'all';
    }

    if (items && items.length > 1) {
      return selectedTypes[0] ?? 'all';
    }

    return '';
  };

  return (
    <FormControl variant="filled" sx={{ width: '100%' }}>
      <InputLabel
        className={`${isDisabled ? classes.labelDisabled : ''}`}
        id="entity-type-select-label"
      >
        Type
      </InputLabel>
      <Select
        labelId="entity-type-select-label"
        id="entity-type-select"
        IconComponent={IconComponent}
        value={getValue()}
        onChange={event => {
          setSelectedTypes(
            event.target.value === 'all' ? [] : [event.target.value],
          );
          resetFilters('type');
        }}
        className={classes.select}
        disabled={isDisabled}
        variant="filled"
      >
        {items?.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
